import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileText,
  MessageCircle,
  MousePointerClick,
  Sparkles,
  Zap,
} from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { BRAND } from "@/lib/site";
import { getAttribution } from "@/lib/utm";

/* ────────────────────────────────────────────────────────────────
   METHOD CHOOSER  ·  /apply
   Award-worthy landing that lets a prospect pick HOW they want to
   apply — WhatsApp, on-site chat, or the classic step-by-step form.
   Signature moments:
     • On-load, line-by-line masked hero reveal
     • Slow editorial marquee
     • Three numbered "chapter" method cards with hover 3D tilt
     • Parallax kinetic backdrop
────────────────────────────────────────────────────────────────── */

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  {
    n: "01",
    id: "whatsapp",
    kicker: "The fastest path",
    title: "Apply on WhatsApp",
    body:
      "Skip the form. A pre-filled template opens inside WhatsApp — you answer, tap send, Raj calls back.",
    minutes: "90 seconds",
    href: "/apply-whatsapp",
    color: "#25D366",
    accent: "bg-[#25D366]",
    dark: "bg-[#0b3a26]",
    icon: MessageCircle,
    tag: "Most chosen",
  },
  {
    n: "02",
    id: "chat",
    kicker: "The conversational path",
    title: "Chat with Swift Assist",
    body:
      "One question at a time, right on this page. Feels like texting a friend — no form fields, no forgotten boxes.",
    minutes: "~2 minutes",
    href: "/apply-chat",
    color: "#C86241",
    accent: "bg-brand-terracotta",
    dark: "bg-brand-forest",
    icon: Sparkles,
    tag: "Guided",
  },
  {
    n: "03",
    id: "form",
    kicker: "The precise path",
    title: "The classic 5-step form",
    body:
      "For when you have your numbers ready — turnover, EMIs, PAN. Structured, submitable, printable.",
    minutes: "4-5 minutes",
    href: "/apply-form",
    color: "#D6A44E",
    accent: "bg-brand-gold",
    dark: "bg-brand-forest",
    icon: FileText,
    tag: "Detailed",
  },
];

const MANIFESTO = [
  "Bangalore since 2018",
  "15+ lender partners",
  "48-hour approvals",
  "Transparent fees",
  "Zero paperwork chase",
  "One trusted advisor",
];

export default function ApplyChooser() {
  const [params] = useSearchParams();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const parallaxScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.08]);

  // Preserve incoming query (e.g. ?type=home_loan&amount=5000000) for the classic form
  const formHref = useMemo(() => {
    const qs = params.toString();
    return qs ? `/apply-form?${qs}` : "/apply-form";
  }, [params]);

  // Track a lightweight "chooser-view" ping — helpful for attribution downstream (fire-and-forget)
  useEffect(() => {
    try {
      const attr = getAttribution();
      if (attr && attr.utm_source) {
        // no-op: attribution captured, will attach on final submit
      }
    } catch (e) {
      // silent
    }
  }, []);

  return (
    <div data-testid="apply-chooser-page" className="bg-brand-cream text-brand-forest">
      {/* ─── HERO ────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative overflow-hidden isolate bg-brand-forest text-brand-cream"
      >
        {/* Parallax backdrop */}
        <motion.div
          aria-hidden
          style={{ y: parallaxY, scale: parallaxScale }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute -top-40 -left-40 h-[70vh] w-[70vh] rounded-full bg-brand-terracotta/25 blur-[120px]" />
          <div className="absolute -bottom-32 -right-24 h-[60vh] w-[60vh] rounded-full bg-brand-gold/20 blur-[130px]" />
          <div className="absolute inset-0 bg-grain opacity-25" />
          {/* Grid lines */}
          <svg
            className="absolute inset-0 h-full w-full opacity-[0.09]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern id="ap-grid" width="64" height="64" patternUnits="userSpaceOnUse">
                <path d="M 64 0 L 0 0 0 64" fill="none" stroke="#F5E9C8" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ap-grid)" />
          </svg>
        </motion.div>

        <div className="container-x relative z-10 pt-14 pb-24 md:pt-20 md:pb-32">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-gold/90"
            data-testid="chooser-eyebrow"
          >
            <span className="h-px w-8 bg-brand-gold/60" />
            <span>Chapter 00 · Begin your application</span>
          </motion.div>

          {/* Masked line-by-line reveal */}
          <h1 className="mt-8 font-outfit font-extrabold leading-[0.96] tracking-tight text-[13.5vw] sm:text-[9vw] md:text-[7.4vw] lg:text-[6.6vw]">
            <RevealLine delay={0.15}>Pick your</RevealLine>
            <RevealLine delay={0.32}>
              <span className="italic font-medium text-brand-gold">way in.</span>
            </RevealLine>
            <RevealLine delay={0.5}>
              <span className="text-brand-cream/60">Three paths.</span>
            </RevealLine>
            <RevealLine delay={0.66}>
              <span className="text-brand-cream/60">One outcome —</span>
              <span className="text-brand-cream"> a call from Raj.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 max-w-xl text-base sm:text-lg text-brand-cream/75 leading-relaxed"
          >
            However you want to apply — WhatsApp, chat or the classic form — your
            file lands in the same inbox. Same one person reviews it. Same one
            person calls you back.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
            className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-brand-cream/55"
          >
            <span className="inline-flex items-center gap-2">
              <MousePointerClick className="h-3.5 w-3.5 text-brand-gold" /> Choose below
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Est. reading 20 seconds</span>
          </motion.div>
        </div>

        {/* Editorial marquee — slow, sarif-adjacent, near bottom of hero */}
        <div
          aria-hidden
          data-testid="chooser-marquee"
          className="relative z-10 border-y border-brand-cream/10 py-5 md:py-6 overflow-hidden"
        >
          <div className="flex whitespace-nowrap animate-marquee-slow">
            {[...MANIFESTO, ...MANIFESTO, ...MANIFESTO].map((m, i) => (
              <span
                key={i}
                className="mx-8 inline-flex items-center gap-4 font-outfit italic text-2xl md:text-3xl text-brand-cream/80"
              >
                {m}
                <span className="text-brand-terracotta">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CHOOSER CHAPTERS ────────────────────────────── */}
      <section className="container-x pt-16 md:pt-24 pb-14 md:pb-20">
        <div className="grid md:grid-cols-12 gap-6 items-end mb-10 md:mb-14">
          <div className="md:col-span-7">
            <span className="eyebrow">The three paths</span>
            <h2 className="mt-4 h-display text-3xl sm:text-4xl md:text-5xl">
              How would you like to<br /> tell us about yourself?
            </h2>
          </div>
          <p className="md:col-span-5 text-brand-sage leading-relaxed">
            Everyone&apos;s different. Some want to type on WhatsApp between meetings.
            Some want to talk it through. Some want a proper form. Take the one
            that fits you today — you can always switch.
          </p>
        </div>

        <div
          data-testid="chooser-chapters"
          className="grid gap-6 md:gap-7 lg:grid-cols-3 md:grid-cols-2 grid-cols-1"
        >
          {CHAPTERS.map((c, i) => {
            const href = c.id === "form" ? formHref : c.href;
            return (
              <MethodChapter key={c.id} chapter={c} href={href} index={i} />
            );
          })}
        </div>
      </section>

      {/* ─── SIGNATURE BAND ──────────────────────────────── */}
      <section className="bg-brand-forest text-brand-cream py-20 md:py-28 relative overflow-hidden">
        <div aria-hidden className="absolute inset-0">
          <div className="absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-brand-gold/15 blur-3xl" />
          <div className="absolute right-0 bottom-0 h-64 w-64 rounded-full bg-brand-terracotta/20 blur-3xl" />
          <div className="absolute inset-0 bg-grain opacity-15" />
        </div>
        <div className="container-x relative">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <span className="text-xs uppercase tracking-[0.22em] text-brand-gold">
                Personal note · from Raj
              </span>
              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: EASE }}
                className="mt-6 font-outfit font-semibold text-2xl sm:text-3xl md:text-4xl leading-snug tracking-tight"
              >
                “I don&apos;t just want your form.
                <span className="text-brand-gold italic font-medium"> I want the context</span> — 
                what you&apos;re trying to build, why now, what you&apos;ve tried. That&apos;s how I get you the right lender the first time.”
              </motion.blockquote>
              <div className="mt-6 flex items-center gap-4">
                <div className="h-11 w-11 rounded-full overflow-hidden ring-2 ring-brand-gold/40">
                  <img
                    src={BRAND.founderPhoto}
                    alt={BRAND.founderName}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-outfit font-bold text-sm">{BRAND.founderName}</div>
                  <div className="text-xs text-brand-cream/60">{BRAND.founderTitle}</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="rounded-3xl border border-brand-cream/15 bg-brand-cream/5 backdrop-blur-md p-7 md:p-8">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-gold">
                  <Clock className="h-3.5 w-3.5" /> What happens next
                </div>
                <ol className="mt-5 space-y-4">
                  {[
                    "You submit — through any of the three paths.",
                    `${BRAND.founderName.split(" ")[0]} personally reviews within the hour.`,
                    "You get a callback from " + BRAND.phone + " within one business day.",
                    "We shortlist 2-3 lenders, negotiate rates, and take it from there.",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-brand-cream/85">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-gold text-brand-forest text-[11px] font-bold">
                        {i + 1}
                      </span>
                      {t}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTNOTE ────────────────────────────────────── */}
      <section className="container-x py-14 md:py-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-sand bg-white px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-terracotta">
            <Zap className="h-3 w-3" /> Not sure which to pick?
          </div>
          <p className="mt-5 text-brand-sage leading-relaxed">
            If you&apos;re on your phone right now, <Link to="/apply-whatsapp" className="text-brand-terracotta font-semibold underline decoration-brand-gold decoration-2 underline-offset-4">start on WhatsApp</Link> — it&apos;s the fastest.
            On desktop and prefer typing? Try the <Link to="/apply-chat" className="text-brand-terracotta font-semibold underline decoration-brand-gold decoration-2 underline-offset-4">on-site chat</Link>.
            Have all your numbers? Head to the <Link to={formHref} className="text-brand-terracotta font-semibold underline decoration-brand-gold decoration-2 underline-offset-4">classic form</Link>.
          </p>
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-brand-sage">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-brand-terracotta" /> No data sold or shared</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-brand-terracotta" /> Consultation is free</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ────────────────── LINE REVEAL (masked) ─────────────── */
function RevealLine({ children, delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={reduce ? { y: 0 } : { y: "100%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: EASE }}
        className="block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

/* ────────────────── METHOD CHAPTER CARD ──────────────── */
function MethodChapter({ chapter, href, index }) {
  const reduce = useReducedMotion();
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const Icon = chapter.icon;

  const onMove = (e) => {
    if (reduce) return;
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -6, y: px * 6 });
  };
  const reset = () => setTilt({ x: 0, y: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: EASE }}
      className="[perspective:1200px]"
    >
      <Link
        ref={cardRef}
        to={href}
        onMouseMove={onMove}
        onMouseLeave={reset}
        data-testid={`chooser-card-${chapter.id}`}
        className="group relative block rounded-[28px] overflow-hidden bg-white border border-brand-sand/70 transition-shadow duration-500 hover:shadow-[0_40px_80px_-30px_rgba(17,42,34,0.35)]"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transformStyle: "preserve-3d",
          transition: "transform 400ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Colored accent block */}
        <div
          className={`relative ${chapter.dark} text-brand-cream px-7 pt-7 pb-14 overflow-hidden`}
        >
          <div
            aria-hidden
            className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-2xl opacity-40"
            style={{ backgroundColor: chapter.color }}
          />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-cream/60">
                Chapter
              </div>
              <div className="mt-1 font-outfit font-extrabold text-6xl sm:text-7xl leading-none tracking-tight">
                {chapter.n}
              </div>
            </div>
            <div
              className={`grid h-12 w-12 place-items-center rounded-2xl ${chapter.accent} text-white shadow-lg transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110`}
            >
              <Icon className="h-5 w-5" />
            </div>
          </div>
          <div className="relative mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold">
            <span className="rounded-full bg-brand-cream/10 backdrop-blur px-2.5 py-1">
              {chapter.tag}
            </span>
            <span className="text-brand-cream/50">·</span>
            <span className="text-brand-cream/70">{chapter.minutes}</span>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-7 bg-white">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: chapter.color }}>
            {chapter.kicker}
          </div>
          <h3 className="mt-2 font-outfit font-extrabold text-2xl md:text-[26px] leading-tight tracking-tight text-brand-forest">
            {chapter.title}
          </h3>
          <p className="mt-3 text-sm text-brand-sage leading-relaxed">
            {chapter.body}
          </p>

          <div className="mt-6 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest group-hover:text-brand-terracotta transition-colors">
              Begin here
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
            </span>
            <ArrowUpRight
              className="h-5 w-5 text-brand-sage/60 group-hover:text-brand-terracotta transition-colors"
              aria-hidden
            />
          </div>
        </div>

        {/* Bottom hover bar */}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700"
          style={{ backgroundColor: chapter.color }}
        />
      </Link>
    </motion.div>
  );
}
